import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, IconButton, Paper, Popover } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PatchedAdapterDateFns } from 'providers/PatchedAdapterDateFns'
import T from 'T'
import HHDateCalendarDay from './HHDateCalendarDay'

const HHDateCalendar = ({
  isActive,
  heatmap,
  dayMetaByDate,
  isLoading,
  showLoading = true,
  expandWindowIfNeeded,
  ensureDateInWindow,
  value,
  onChange,
  onMonthChange,
  legend = null,
  elevation = 1,
}) => {
  const effectiveLoading = showLoading && isLoading
  const [visibleMonth, setVisibleMonth] = useState(() => value || new Date())
  const [legendAnchorEl, setLegendAnchorEl] = useState(null)
  const isLegendOpen = Boolean(legendAnchorEl)

  const handleOpenLegend = useCallback(e => setLegendAnchorEl(e.currentTarget), [])
  const handleCloseLegend = useCallback(() => setLegendAnchorEl(null), [])

  const handleDateChange = useCallback(
    newDate => {
      if (!newDate) return
      onChange?.(newDate)
    },
    [onChange]
  )

  const handleMonthChange = useCallback(
    newMonth => {
      setVisibleMonth(newMonth)
      expandWindowIfNeeded?.(newMonth)
      onMonthChange?.(newMonth)
    },
    [expandWindowIfNeeded, onMonthChange]
  )

  useEffect(() => {
    setVisibleMonth(value || new Date())
  }, [value])

  useEffect(() => {
    if (isActive) {
      ensureDateInWindow?.(visibleMonth)
    }
  }, [isActive, visibleMonth, ensureDateInWindow])

  return (
    <Paper elevation={elevation} sx={{ position: 'relative' }}>
      {legend && (
        <>
          <IconButton
            size="small"
            onClick={handleOpenLegend}
            aria-label={isLegendOpen ? T.HIDE_LEGEND : T.SHOW_LEGEND}
            title={isLegendOpen ? T.HIDE_LEGEND : T.SHOW_LEGEND}
            sx={{ color: 'text.secondary', position: 'absolute', top: 2, left: 2, zIndex: 1 }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Popover
            open={isLegendOpen}
            anchorEl={legendAnchorEl}
            onClose={handleCloseLegend}
            disablePortal
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 1.5 } } }}
          >
            {legend}
          </Popover>
        </>
      )}
      <Box sx={{ minWidth: 320, pt: 1, px: elevation > 0 ? 1.5 : 0 }}>
        <LocalizationProvider dateAdapter={PatchedAdapterDateFns}>
          <DateCalendar
            value={value || new Date()}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            renderLoading={() => <DayCalendarSkeleton />}
            loading={effectiveLoading}
            showDaysOutsideCurrentMonth={false}
            slots={{ day: HHDateCalendarDay }}
            slotProps={{ day: { heatmap, dayMetaByDate } }}
            sx={{
              width: '100%',
              maxHeight: 'none',
              '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
                justifyContent: 'space-between',
              },
              '& .MuiDayCalendar-monthContainer': {
                px: elevation > 0 ? 1.5 : 0,
              },
            }}
          />
        </LocalizationProvider>
      </Box>
      {effectiveLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
    </Paper>
  )
}

HHDateCalendar.propTypes = {
  isActive: PropTypes.bool,
  heatmap: PropTypes.object,
  dayMetaByDate: PropTypes.object,
  isLoading: PropTypes.bool,
  showLoading: PropTypes.bool,
  expandWindowIfNeeded: PropTypes.func,
  ensureDateInWindow: PropTypes.func,
  value: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
  onMonthChange: PropTypes.func,
  legend: PropTypes.node,
  elevation: PropTypes.number,
}

export default HHDateCalendar
